<template>
    <div class="automation-wizard-field-select">
        <CompassSkeleton
            v-show="showSkeleton"
            :size="CompassEnums.SkeletonSize.EXTRA_LARGE"
            class="automation-wizard-field-select__skeleton"
        />
        <CompassFormItem
            v-show="!showSkeleton"
            v-bind="formItemProps"
            :error="field.currentError || ''"
        >
            <CompassSelect
                v-bind="selectProps"
                :key="`field-select-${options.length}`"
                :modelValue="currentValue"
                :loading="loading"
                :disabled="disabled"
                menuClass="automation-wizard-field-select"
                @update:model-value="handleValueChange"
            >
                <CompassDropdownOption
                    v-for="option in options"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                    :dotColor="option.dotColor"
                    :avatar="option.avatar"
                    :type="option.type"
                />
            </CompassSelect>
        </CompassFormItem>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, inject } from 'vue';
import type { Translator } from 'types/injects/translator.type';
import {
    getStaffs,
    getTaskLabels,
    getOpportunitySources,
} from 'api/automation/automationService.js';
import { formatUserToCompassAvatar } from 'Helpers/avatarHelper';

interface SelectFieldKeyValue {
    key: string | number;
    value: string;
}

interface SelectFieldProps {
    formItem?: Record<string, unknown>;
    select?: Record<string, unknown> & { multiple?: boolean };
    mappedFields?: Record<string, string>;
}

interface SelectField {
    id: string;
    type: string;
    required?: boolean;
    disabled?: boolean;
    currentError?: string;
    objectType?: string;
    values?: SelectFieldKeyValue[];
    prependValues?: SelectFieldKeyValue[];
    props?: SelectFieldProps;
}

interface CompassAvatarOption {
    initials: string;
    colorType: string;
    photo: string;
}

interface SelectOption {
    label: string;
    value: string | number;
    dotColor?: string;
    type?: string;
    avatar?: CompassAvatarOption;
    isDefault?: boolean;
}

type ModelValue = string | number | Record<string, unknown> | (string | number)[];

interface Staff {
    id: number;
    firstname: string;
    lastname: string;
    avatar: string | null;
}

interface TaskLabel {
    id: number;
    name: string;
    color: string | null;
    is_active: boolean;
}

interface OpportunitySource {
    id: number;
    name: string;
    is_default: boolean;
}

interface Props {
    field: SelectField;
    modelValue?: ModelValue;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
});

const emit = defineEmits<{
    'update:modelValue': [value: ModelValue];
}>();

const $t = inject('$t') as Translator;

const options = ref<SelectOption[]>([]);
const loading = ref(false);

const formItemProps = computed<Record<string, unknown>>(() => ({
    ...props.field.props?.formItem,
    label: (props.field.props?.formItem?.label as string) || '',
    required: props.field.required || false,
    requiredLabel:
        (props.field.props?.formItem?.requiredLabel as string) || $t.__('common.labels.required'),
}));

const selectProps = computed<Record<string, unknown>>(() => ({
    ...props.field.props?.select,
}));

const disabled = computed<boolean>(() => props.field.disabled || false);

const showSkeleton = computed<boolean>(() => loading.value && options.value.length === 0);

const validOptionValues = computed<Set<string | number>>(
    () => new Set(options.value.map((option) => option.value)),
);

const filterValidValues = (values: unknown): (string | number)[] => {
    if (!Array.isArray(values)) {
        return [];
    }
    return values.filter(
        (value) => value !== null && value !== undefined && validOptionValues.value.has(value),
    );
};

const currentValue = computed<ModelValue>(() => {
    if (selectProps.value?.multiple) {
        return filterValidValues(props.modelValue);
    }
    return validOptionValues.value.has(props.modelValue as string | number) ? props.modelValue : '';
});

const handleValueChange = (newValue: ModelValue): void => {
    const cleanValue = selectProps.value?.multiple
        ? filterValidValues(newValue)
        : validOptionValues.value.has(newValue as string | number)
          ? newValue
          : '';
    emit('update:modelValue', cleanValue);
};

const loadStaffsOptions = async (): Promise<SelectOption[]> => {
    const response = await getStaffs();
    return (response.data as Staff[]).map((staff) => ({
        label: `${staff.firstname} ${staff.lastname}`,
        value: staff.id,
        type: 'avatar',
        avatar: formatUserToCompassAvatar(staff),
    }));
};

const loadLabelTasksOptions = async (): Promise<SelectOption[]> => {
    const response = await getTaskLabels();
    return (response.data as TaskLabel[])
        .filter((label) => label.is_active)
        .map((label) => ({
            label: label.name,
            value: label.id,
            dotColor: label.color ? `#${label.color}` : '',
            type: 'dot',
        }));
};

const loadOpportunitySourcesOptions = async (): Promise<SelectOption[]> => {
    const response = await getOpportunitySources();
    return (response.data as OpportunitySource[]).map((source) => ({
        label: source.name,
        value: source.id,
        isDefault: !!source.is_default,
    }));
};

const loadOptionsFromApi = async (objectType: string): Promise<SelectOption[]> => {
    switch (objectType) {
        case 'staffs':
            return await loadStaffsOptions();
        case 'labelTask':
            return await loadLabelTasksOptions();
        case 'sourceOpp':
            return await loadOpportunitySourcesOptions();
        default:
            return [];
    }
};

const loadOptions = async (): Promise<void> => {
    try {
        loading.value = true;

        const prependOptions: SelectOption[] = Array.isArray(props.field.prependValues)
            ? props.field.prependValues.map((item) => ({
                  label: item.value,
                  value: item.key,
              }))
            : [];

        const fieldOptions: SelectOption[] = Array.isArray(props.field.values)
            ? props.field.values.map((item) => ({
                  label: item.value,
                  value: item.key,
              }))
            : [];

        const apiOptions = props.field.objectType
            ? await loadOptionsFromApi(props.field.objectType)
            : [];

        const sortedOptions = [...fieldOptions, ...apiOptions].sort((a, b) =>
            a.label.localeCompare(b.label),
        );

        options.value = [...prependOptions, ...sortedOptions];

        if (props.field.objectType === 'sourceOpp' && !props.modelValue) {
            const defaultOption = apiOptions.find((option) => option.isDefault);
            if (defaultOption) {
                emit('update:modelValue', defaultOption.value);
            }
        }
    } catch (error) {
        options.value = [];
        throw error;
    } finally {
        loading.value = false;
    }
};

watch(
    () => props.modelValue,
    async () => {
        if (options.value.length === 0) {
            await loadOptions();
        }
    },
);

onMounted(async () => {
    await loadOptions();
});
</script>

<style lang="scss" scoped>
.automation-wizard-field-select {
    &__skeleton {
        width: 100%;
        :deep(.compass-skeleton__line--x-large) {
            height: 56px;
        }
    }
}
</style>

<style lang="scss">
#compass-dropdown-container {
    .automation-wizard-field-select {
        z-index: 1001;
    }
}
</style>
